import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../hooks/axios';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import AdminPageShell from '../../components/admin/AdminPageShell';
import AdminCard from '../../components/admin/AdminCard';
import AdminFormActions from '../../components/admin/AdminFormActions';
import InputWithLabel from '../../components/form/input/InputWithLabel';
import {
  adminAlertErrorClass,
  adminFormFullWidthClass,
  adminFormGridClass,
} from '../../components/admin/adminStyles';
import { siteLabel, type SeoRouteRecord } from './seoRouteTypes';

type FormState = {
  metaTitle: string;
  metaDescription: string;
  h1: string;
  intro: string;
  keywords: string;
  published: boolean;
  sortOrder: number;
  distanceMiles: string;
  driveLabel: string;
};

const INITIAL: FormState = {
  metaTitle: '',
  metaDescription: '',
  h1: '',
  intro: '',
  keywords: '',
  published: true,
  sortOrder: 0,
  distanceMiles: '',
  driveLabel: '',
};

export default function SeoRouteForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState<SeoRouteRecord | null>(null);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    axiosInstance
      .get(`/seo-routes/${id}`)
      .then((res) => {
        const data = res.data as SeoRouteRecord;
        setRoute(data);
        setForm({
          metaTitle: data.seo.metaTitle ?? '',
          metaDescription: data.seo.metaDescription ?? '',
          h1: data.seo.h1 ?? '',
          intro: data.seo.intro ?? '',
          keywords: (data.seo.keywords ?? []).join(', '),
          published: data.published ?? true,
          sortOrder: data.sortOrder ?? 0,
          distanceMiles:
            data.seo.distanceMiles != null ? String(data.seo.distanceMiles) : '',
          driveLabel: data.seo.driveLabel ?? '',
        });
      })
      .catch(() => setError('Failed to load route'));
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : type === 'number'
            ? value
            : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      await axiosInstance.put(`/seo-routes/${id}`, {
        metaTitle: form.metaTitle.trim() || null,
        metaDescription: form.metaDescription.trim() || null,
        h1: form.h1.trim() || null,
        intro: form.intro.trim() || null,
        keywords: form.keywords
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean),
        published: form.published,
        sortOrder: Number(form.sortOrder) || 0,
        distanceMiles:
          form.distanceMiles.trim() === ''
            ? null
            : Number(form.distanceMiles),
        driveLabel: form.driveLabel.trim() || null,
      });
      navigate('/seo-routes');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })
        ?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminPageShell>
      <PageBreadcrumb
        pageTitle="Edit SEO Route"
        description={
          route
            ? `${siteLabel(route.siteKey)} · ${route.from.name} → ${route.to.name} · ${route.path}`
            : 'Update route SEO overrides'
        }
      />

      <AdminCard>
        {error && <div className={`mb-4 ${adminAlertErrorClass}`}>{error}</div>}
        <form onSubmit={handleSubmit} className={`${adminFormGridClass} w-full`}>
          <div className={adminFormFullWidthClass}>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Leave fields blank to use auto-generated SEO from place names. Saved
              values override the defaults on all brand sites for this corridor.
            </p>
          </div>

          <InputWithLabel
            label="Meta title"
            name="metaTitle"
            value={form.metaTitle}
            onChange={handleChange}
          />
          <div className={adminFormFullWidthClass}>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Meta description
            </label>
            <textarea
              name="metaDescription"
              value={form.metaDescription}
              onChange={handleChange}
              rows={3}
              className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>
          <InputWithLabel
            label="H1"
            name="h1"
            value={form.h1}
            onChange={handleChange}
          />
          <div className={adminFormFullWidthClass}>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Intro paragraph
            </label>
            <textarea
              name="intro"
              value={form.intro}
              onChange={handleChange}
              rows={4}
              className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>
          <div className={adminFormFullWidthClass}>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Keywords (comma-separated)
            </label>
            <textarea
              name="keywords"
              value={form.keywords}
              onChange={handleChange}
              rows={2}
              className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>
          <InputWithLabel
            type="number"
            label="Distance (miles)"
            name="distanceMiles"
            value={form.distanceMiles}
            onChange={handleChange}
          />
          <InputWithLabel
            label="Drive time label"
            name="driveLabel"
            value={form.driveLabel}
            onChange={handleChange}
            placeholder="e.g. 1 hr 30 min"
          />
          <InputWithLabel
            type="number"
            label="Sort order"
            name="sortOrder"
            value={String(form.sortOrder)}
            onChange={handleChange}
          />
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 sm:col-span-2 xl:col-span-3">
            <input
              type="checkbox"
              name="published"
              checked={form.published}
              onChange={handleChange}
            />
            <span>Published</span>
          </label>

          <div className={adminFormFullWidthClass}>
            <AdminFormActions listRoute="/seo-routes" saving={loading} />
          </div>
        </form>
      </AdminCard>
    </AdminPageShell>
  );
}
